/* eslint-disable no-unused-vars */
import React, {
    useContext,
    useEffect,
    useRef,
    useState,
  } from "react";
  import GlobalConfig from "../context/GlobalConfig";
  import {
    ArrowRightIcon,
    CheckIcon,
  } from "@heroicons/react/24/solid";
  import Footer from "../components/Footer";
  import Head from "./Head";
  import { countries } from "../constants/countries";
  import { languageData } from "../constants/languageData";
  import { useParams } from "react-router-dom";
  import JourneyConfig from "../context/JourneyConfig";
  import Loader from "../components/Loader/Loader";
  import RenderSvg from "../components/RenderSvg";
  import GoogleIcon from "../components/GoogleIcon";
  import { findNextStep } from "../utils/orchestrationModule";

  function IntroLiveness({ stepperFunc, steps, currentStep }) {
    const { journey, setJourney, getJourney, updateJourney } =
      useContext(JourneyConfig);
    const { config, setConfig, routesAndNavigation } = useContext(GlobalConfig);
    const { branding } = config ?? {};
    const language = sessionStorage.getItem("language") ?? "en";
    // const { faceCapture } = config ?? {};
    const { faceCapture } = config ?? {};
    const [consentStatus, setConsentStatus] = useState(
      journey?.capturedData?.livenessConsent ?? false
    );
    const [showDeclineConfirm, setShowDeclineConfirm] = useState(false);
    const { journeyId } = useParams();
    const [loading, setLoading] = useState(true);
    const color = config?.branding?.accentColor;
    const bgColor =
      "color-mix(in srgb, " + config?.branding?.accentColor + " 10%, white)";
  
    useEffect(() => {
      let timer = setTimeout(() => {
        setLoading(false);
      }, 1000); // Display loader for at least one second
  
      // if (journeyId && !journey) {
      //   getJourney(journeyId).then(() => {
      //     clearTimeout(timer); // Clear the timer if data is loaded before one second
      //     setLoading(false);
      //   });
      // }
  
      return () => clearTimeout(timer); // Clear the timer if component unmounts
    }, [journeyId, journey, getJourney]);
  
    useEffect(() => {
      if (journey) {
        setJourney({
          ...journey,
          capturedData: { ...journey?.capturedData, livenessConsent: consentStatus },
        });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [consentStatus]);
  
  
    const moveToNextStep = async () => {
      if(!faceCapture?.privacyPolicyLink){
        await updateJourney("LIVENESS_CONSENT", { livenessConsent: false },currentStep);
      }else{
        await updateJourney("LIVENESS_CONSENT", { livenessConsent: true },currentStep);
      }
      stepperFunc(steps[0]);
    };

    // Biometric Consent with Decline Option (IDX-676): when enabled on the flow, the
    // applicant must explicitly consent or decline before the liveness check.
    const biometricConsentEnabled = faceCapture?.biometricConsent === true;
    const consentText =
      languageData[language]?.biometricConsent ??
      languageData["en"]?.biometricConsent;

    // Consent given: record it and continue to the selfie/liveness step as today.
    const proceedToSelfie = async () => {
      await updateJourney("LIVENESS_CONSENT", { livenessConsent: true }, currentStep);
      setJourney({ ...journey, capturedData: { ...journey?.capturedData, livenessConsent: true } });
      // Clear any prior decline flag if the applicant returns and consents instead.
      sessionStorage.removeItem("biometricDeclined");
      stepperFunc(steps[0]);
    };

    // Consent declined: record it, then skip selfie / liveness / face match entirely and
    // jump straight to the completion screen (the camera is never opened).
    const confirmDecline = async () => {
      await updateJourney("LIVENESS_CONSENT", { livenessConsent: false }, currentStep);
      setJourney({ ...journey, capturedData: { ...journey?.capturedData, livenessConsent: false } });
      // Persist the decline so the completion screen can still show the decline message after a
      // page refresh, when sessionId is cleared and journey/config can no longer be restored (SA-33713).
      sessionStorage.setItem("biometricDeclined", "true");
      const { routes } = routesAndNavigation ?? {};
      const completeStep = findNextStep(currentStep, "complete", routes ?? {});
      // findNextStep returns 0 both for "match at index 0" and "not found"; the complete
      // route is always present post-document (never index 0), so a 0 here means it could
      // not be resolved — advance forward instead of bouncing back to the first step.
      stepperFunc(completeStep > 0 ? completeStep : steps[0]);
    };

    if (loading) {
      return <Loader />;
    }

    // Biometric consent declined: show a full-screen confirmation page (not a popup),
    // matching the design. Reuses the same Head + illustration + Footer chrome as the
    // instruction page below. "Go back" returns to the instruction page; "Confirm"
    // records the decline and skips selfie / liveness / face match.
    if (showDeclineConfirm) {
      return (
        <>
          <Head
            style={{ zIndex: "100" }}
            stepperFunc={stepperFunc}
            showMobileFlow={false}
            showCountry={false}
            showLanguages={false}
            showBackIcon={false}
          />
          <div className="parent-container">
            <div className="main">
              <div className="left-section">
                <RenderSvg config={config} svgType={"faceIntro"} id="faceIntro" />
              </div>
              <div
                className="right-section md:items-start items-center"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  gap: 24,
                }}
              >
                <div className="flex flex-col">
                  <div className="text-[18px] md:text-[24px] font-semibold text-[#000000] text-center md:text-start">
                    {consentText?.confirmTitle}
                  </div>
                  <div className="mt-4 text-[12px] md:text-[16px] text-[#656565] text-center md:text-start leading-[22px]">
                    {consentText?.confirmBody}
                  </div>
                </div>
                <div
                  className="flex flex-col justify-center md:justify-end items-center w-full"
                  style={{ fontFamily: "Inter, sans-serif" }}
                >
                  <div className="flex flex-col items-center gap-[8px] w-full md:px-[0px] px-[24px]">
                    <button
                      className="flex items-center justify-center w-full pt-[12px] pb-[12px] pr-[24px] pl-[24px] text-white rounded-[4px] text-[14px] font-medium"
                      style={{ backgroundColor: color }}
                      onClick={() => setShowDeclineConfirm(false)}
                    >
                      {consentText?.goBack}
                    </button>
                    <button
                      className="w-full pt-[12px] pb-[12px] text-[14px] font-medium bg-transparent text-[#656565]"
                      onClick={() => confirmDecline()}
                    >
                      {consentText?.confirmDecline}
                    </button>
                    <Footer />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      );
    }

    return (
      <>
        <Head
          style={{ zIndex: "100" }}
          stepperFunc={stepperFunc}
          showMobileFlow={false}
          showCountry={false}
          showLanguages={false}
          showBackIcon={false}
        />
        <div className="parent-container">
          <div className="main">
            <div className="left-section">
              <RenderSvg
                config={config}
                svgType={"faceIntro"}
                id="faceIntro"
              />
            </div>
            <div
              className="right-section md:items-start items-center"
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                gap: 24,
              }}
            >
              <div className="flex flex-col ">
                <div className="text-[18px] md:text-[24px] font-semibold text-[#000000] text-center md:text-start md:gap-[16px]">
                  {languageData[language]?.introLiveness?.heading}
                </div>
                <div className="mt-2 text-[12px] md:text-[14px] font-semibold text-[#656565] text-center md:text-start">
                  {languageData[language]?.introLiveness?.subHeading ?
                  languageData[language]?.introLiveness?.subHeading : `You're just one step away` }
                </div>
              </div>
  
              <div className="flex flex-col mt-2 ">
                <div className="flex flex-col md:gap-[16px] gap-3">
                  <div className="flex flex-row gap-[12px] md:w-[100%] items-center">
                    <div>
                      <div
                        className={`px-[8px] py-[8px] rounded-full`}
                        style={{
                          backgroundColor: bgColor,
                        }}
                      >
                        {(
                          <GoogleIcon
                            iconName="wb_sunny"
                            size={20}
                            style={{ color: color }}
                          />
                        )}
                      </div>
                    </div>
                    <div className="text-[14px] text-[#656565] leading-[20px]">
                      {languageData[language]?.introLiveness?.instruction1}
                    </div>
                  </div>
                  <div className="flex flex-row gap-[12px] md:w-[100%] items-center">
                    <div>
                      <div
                        className={`px-[8px] py-[8px] rounded-full`}
                        style={{ backgroundColor: bgColor }}
                      >
                        <GoogleIcon
                          iconName="center_focus_weak"
                          size={20}
                          style={{ color: color }}
                        />
                      </div>
                    </div>
                    <div
                      className="text-[14px] text-[#656565]"
                      style={{
                        fontWeight: 400,
                        color: "#656565",
                      }}
                    >
                      {languageData[language]?.introLiveness?.instruction2}
                      </div>
                  </div>
                  <div className="flex flex-row gap-[12px] md:w-[100%] items-center">
                    <div>
                      <div
                        className={`px-[8px] py-[8px] rounded-full`}
                        style={{ backgroundColor: bgColor }}
                      >
                        <GoogleIcon
                          iconName="masks"
                          size={20}
                          style={{ color: color }}
                        />
                      </div>
                    </div>
                    <div
                      className="text-[14px] text-[#656565]"
                      style={{
                        fontWeight: 400,
                        color: "#656565",
                      }}
                    >
                      {languageData[language]?.introLiveness?.instruction3}
                      </div>
                  </div>
                </div>
              </div>
  
              <div
                className="flex flex-col justify-center md:justify-end md:items-start items-center mt-[0.5rem] md:gap-[16px] w-full"
                style={{ fontFamily: "Inter, sans-serif" }}
              >
                {biometricConsentEnabled ? (
                  <div className="flex flex-col justify-end items-start gap-[16px] md:px-[0px] px-[24px] w-full">
                    {/* Biometric consent statement + privacy policy link */}
                    <div className="text-[12px] text-[#656565] leading-[18px] self-stretch bg-[#F9FAFB] rounded-[8px] p-[12px] border border-[#EEF0F2]">
                      {consentText?.consentStatement}
                      {faceCapture?.privacyPolicyLink && (
                        <>
                          {" "}
                          <a
                            href={faceCapture?.privacyPolicyLink}
                            className="hover:underline font-semibold"
                            target="_blank"
                            rel="noreferrer"
                            style={{ color: color }}
                          >
                            {consentText?.privacyPolicy}
                          </a>
                        </>
                      )}
                    </div>
                    <div className="flex flex-col items-center gap-[8px] w-full">
                      <button
                        className="flex items-center justify-center gap-[8px] w-full pt-[12px] pb-[12px] pr-[24px] pl-[24px] text-white rounded-[4px] text-[14px] font-medium"
                        style={{ backgroundColor: color }}
                        onClick={() => proceedToSelfie()}
                      >
                        {consentText?.consentButton}
                        <ArrowRightIcon className="h-[16px] w-[16px]" />
                      </button>
                      <button
                        className="w-full pt-[12px] pb-[12px] text-[14px] font-medium bg-transparent text-[#656565]"
                        onClick={() => setShowDeclineConfirm(true)}
                      >
                        {consentText?.declineButton}
                      </button>
                    </div>
                    <Footer />
                  </div>
                ) : (
                  <div className="flex flex-col justify-end items-start gap-[16px] md:px-[0px] px-[24px] w-full">
                    <label
                      onClick={(e) => {
                        // Don't toggle checkbox if click is on the link or its children
                        const target = e.target;
                        const isLinkClick = target.tagName === 'A' || target.closest('a');
                        if (!isLinkClick) {
                          setConsentStatus(!consentStatus);
                        }
                      }}
                      style={{
                        cursor: "pointer",
                        display: faceCapture?.privacyPolicyLink
                          ? "flex"
                          : "none",
                      }}
                      className="flex items-start gap-[12px]  self-stretch"
                    >
                      <div
                        className="flex justify-center items-center"
                        style={{
                          border: "1px solid black",
                          width: 22,
                          height: 16,
                          borderRadius: 4,
                          background: consentStatus
                            ? color
                            : "none",
                        }}
                      >
                        <CheckIcon
                          width={12}
                          height={12}
                          color={"white"}
                          style={{
                            display: consentStatus
                              ? "block"
                              : "none",
                          }}
                        />
                      </div>
                      <span className="text-[12px]  text-[#656565]">
                        {
                          languageData[language]?.termsAndCondition
                            ?.title
                        }
                        &nbsp;
                        <a
                          href={faceCapture?.privacyPolicyLink}
                          className={`hover:underline`}
                          target="_blank"
                          rel="noreferrer"
                          style={{ color: color }}
                          onClick={(e) => {
                            e.stopPropagation();
                          }}
                        >
                          {
                            languageData[language]?.termsAndCondition
                              ?.privacyPolicy
                          }
                        </a>
                      </span>
                    </label>
                    <div className="flex flex-col md:justify-end items-center md:gap-[12px] w-full">
                      <button
                        className="flex items-center justify-center  gap-[8px] w-full pt-[12px] pb-[12px] pr-[24px] pl-[24px] text-white rounded-[4px] text-[14px] font-medium"
                        style={{
                          backgroundColor:
                            faceCapture?.privacyPolicyLink &&
                              !consentStatus
                              ? bgColor
                              : color,
                        }}
                        disabled={
                          faceCapture?.privacyPolicyLink &&
                          !consentStatus
                        }
                        onClick={() => moveToNextStep()}
                      >
                        {
                          languageData[language]?.introLiveness
                            ?.button
                        }
                        <ArrowRightIcon className="h-[16px] w-[16px]" />
                      </button>
                    </div>
                    <Footer />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  export default IntroLiveness;
  